import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BarcodeScanner, GoogleBarcodeScannerModuleInstallState } from '@capacitor-mlkit/barcode-scanning';
import { AlertController } from '@ionic/angular/standalone';
import { BarcodeFormat } from '@capacitor-mlkit/barcode-scanning/dist/esm/definitions';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

@Injectable()
export class BarcodeScannerService
{
    private alertController = inject(AlertController);

    async initialize(): Promise<boolean>
    {
        if (!Capacitor.isNativePlatform())
        {
            return true;
        }

        if (Capacitor.getPlatform() !== 'android')
        {
            return true;
        }

        try
        {
            const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
            console.log('isGoogleBarcodeScannerModuleAvailable', available);

            if (available)
            {
                return true;
            }
        }
        catch (error)
        {
            console.log('Error in isGoogleBarcodeScannerModuleAvailable', error);

            return false;
        }

        return new Promise<boolean>(async (resolve, reject) =>
        {
            await BarcodeScanner.addListener('googleBarcodeScannerModuleInstallProgress', (event) =>
            {
                console.log('googleBarcodeScannerModuleInstallProgress', event);

                if (event.state === GoogleBarcodeScannerModuleInstallState.COMPLETED)
                {
                    resolve(true);
                }
            });

            try
            {
                await BarcodeScanner.installGoogleBarcodeScannerModule();
            }
            catch (error)
            {
                console.log('Error in installGoogleBarcodeScannerModule', error);
                await BarcodeScanner.removeAllListeners();
            }
        });
    }

    async isSupported(): Promise<boolean>
    {
        if (!Capacitor.isNativePlatform())
        {
            return false;
        }

        const { supported } = await BarcodeScanner.isSupported();

        return supported;
    }

    async requestPermissions()
    {
        if (!Capacitor.isNativePlatform())
        {
            return false;
        }

        const { camera } = await BarcodeScanner.requestPermissions();
        const granted = camera === 'granted' || camera === 'limited';

        if (!granted)
        {
            await this.presentAlert();
        }

        return granted;
    }

    async scan()
    {
        const isSupported = await this.isSupported();
        const granted = await this.requestPermissions();

        if (isSupported && granted)
        {
            const result = await BarcodeScanner.scan({
                formats: [BarcodeFormat.Ean8, BarcodeFormat.Ean13],
            });

            if (Capacitor.isNativePlatform())
            {
                await Haptics.impact({ style: ImpactStyle.Medium });
            }

            return result.barcodes;
        }

        return [];
    }

    private async presentAlert(): Promise<void>
    {
        const alert = await this.alertController.create({
            header: 'Permission denied',
            message: 'Please grant camera permission in your device settings to use the barcode scanner.',
            buttons: ['OK'],
        });

        await alert.present();
    }
}
